'use client';

import { DestinationCGType, TransportResponseCGType } from '@/types/graphql/root';
import { getDayPinColor, getPoiFallbackImageUrl, getPoiType, mapSlotTypeToPoiType } from '@/utils';
import { useCallback, useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import ImageWithFallback from '../common/image-with-fallback';
import CarPrimary1414 from '@svg/trip/car_primary_14_14.svg';
import AddGray1818 from '@svg/trip/add_gray_18_18.svg';
import CloseGray1010 from '@svg/trip/close_gray_10_10.svg';
import DestHandlePrimary146 from '@svg/trip/dest_handle_primary_14_6.svg';
import SpenttimePrimary1212 from '@svg/trip/spenttime_primary_12_12.svg';
import Star1212 from '@svg/trip/star_12_12.svg';
import client from '@/utils/apollo-client';
import { GetTransportsQuery } from '@/utils/gql';
import LinkForZzimcarAndroid from '../common/link-for-zzimcar-android';
import { TRIP_CONDITIONS, TRIP_MODE, TripModeType } from '@/app/ui/trip/client-trip-page';
import { getPoiImages } from '@/utils/actions';

export interface DestSlotProps {
  dest: {
    prev: DestinationCGType | null;
    cur: DestinationCGType;
    next: DestinationCGType | null;
  };
  mode: TripModeType;
  dayNumber: number;
  destIndex: number;
  isEditRole: boolean;
  isLastDay: boolean;
  isLastDest: boolean;
  onClickPoiDetail: (_dest: DestinationCGType) => void;
  destinations: DestinationCGType[];
  setDestinations: React.Dispatch<React.SetStateAction<DestinationCGType[]>>;
  isSortableDragging: boolean;
  onClickChangeMode: (_nextMode: TripModeType) => void;
  setAddDestinationMode: React.Dispatch<React.SetStateAction<'selectDay' | 'normal'>>;
  setAddDestinationsPosition: (
    _value: React.SetStateAction<{
      day: number;
      pos: number;
      replace?: boolean | undefined;
      replaceDestId?: string | undefined;
    } | null>,
  ) => void;
  onClickRemoveSlot: (_destId: string) => void;
  selectedDestIdList: string[];
  memoDictionary: { [key: string]: string };
  onToggleSelectDest: (_destId: string) => void;
  onUpdateMemo: (_destId: string, _memo: string) => void;
  tripFromDateTime?: string | null;
}

const MAX_RETRY_COUNT = 3;
const RETRY_DELAY_MS = 3000;

export default function DestSlot({
  dest,
  mode,
  dayNumber,
  destIndex,
  isEditRole,
  isLastDay,
  isLastDest,
  onClickPoiDetail,
  destinations,
  isSortableDragging,
  onClickChangeMode,
  setAddDestinationMode,
  setAddDestinationsPosition,
  onClickRemoveSlot,
  selectedDestIdList,
  memoDictionary,
  onToggleSelectDest,
  onUpdateMemo,
  tripFromDateTime,
}: DestSlotProps) {
  const [transport, setTransport] = useState<TransportResponseCGType | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const [poiImageUrl, setPoiImageUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!dest.cur.poi?.latitude || !dest.cur.poi?.longitude) return;
    if (!dest.next?.poi?.latitude || !dest.next?.poi?.longitude) return;

    client
      .query<{
        transports: TransportResponseCGType[];
      }>({
        query: GetTransportsQuery,
        fetchPolicy: 'cache-first',
        variables: {
          coords: [[dest.cur.poi.latitude, dest.cur.poi.longitude, dest.next.poi.latitude, dest.next.poi.longitude]],
        },
      })
      .then((data) => {
        if (data.data?.transports?.[0]) {
          setTransport(data.data.transports[0]);
        } else {
          setTransport(null);
        }
      });
  }, [dest.cur, dest.next]);

  const durationMinutes = useMemo(() => {
    if (!transport) return null;

    if (!transport?.duration) {
      if (!dest.next) return null;
      if (dest.cur?.poi?.id === dest.next?.poi?.id) return null;
      return '0분';
    }

    const min = Math.ceil(transport.duration / 60);
    return min + ' 분';
  }, [transport]);

  const poiImageSrc = useMemo(() => {
    if (poiImageUrl) return poiImageUrl;
    return dest.cur.poi?.images?.[0]?.imageUrl ?? '';
  }, [dest, poiImageUrl]);

  const googleMapUrl = useMemo(() => {
    if (!dest.cur.poi?.latitude || !dest.cur.poi?.longitude || !dest.next?.poi?.latitude || !dest.next?.poi?.longitude)
      return undefined;

    return `https://www.google.com/maps/dir/${dest.cur.poi.latitude},${dest.cur.poi.longitude}/${dest.next.poi.latitude},${dest.next.poi.longitude}`;
  }, [dest.cur, dest.next]);

  const displayedPoiType = useMemo(() => {
    return dest.cur?.slotType ? mapSlotTypeToPoiType(dest.cur.slotType) : dest.cur.poi?.poiType;
  }, [dest?.cur.slotType, dest.cur.poi?.poiType]);

  const rating = useMemo(() => {
    return dest.cur.poi?.ratings?.find((r) => r?.provider === 'google');
  }, [dest.cur.poi?.ratings]);

  const isSelected = useMemo(() => {
    if (!dest.cur.id) return false;
    return selectedDestIdList.includes(dest.cur.id);
  }, [selectedDestIdList]);

  const memo = useMemo(() => {
    const destId = dest.cur.id;
    if (!destId) return '';
    return memoDictionary[destId] ?? '';
  }, [memoDictionary]);

  const onChangeMemo = useCallback(
    (value: string) => {
      const destId = dest.cur.id;
      if (!destId) return;
      onUpdateMemo(destId, value);
    },
    [onUpdateMemo],
  );

  const poiOperatinghourAndPriceComponent = useMemo(() => {
    if (!dest.cur.poi?.operatingHour && !dest.cur.poi?.priceRange) return null;

    let operatingHourComponent: React.ReactNode = null;
    let priceRangeComponent: React.ReactNode = null;

    if (dest.cur.poi?.operatingHour) {
      if (dest.cur.poi.operatingHour.includes('휴무일')) {
        operatingHourComponent = <span className='body-12-md text-[#FF0000]'>휴무일</span>;
      } else {
        operatingHourComponent = (
          <span className='body-12-md flex-none'>
            <span className='text-[#33A1FC]'>영업시간 </span>
            <span className='text-gray-800 body-12-regular'>({dest.cur.poi.operatingHour.replaceAll(' - ', '~')})</span>
          </span>
        );
      }
    }
    if (dest.cur.poi?.priceRange) {
      priceRangeComponent = (
        <div className='body-12-regular text-gray-800 truncate'>약 {dest.cur.poi.priceRange.toLocaleString()}원</div>
      );
    }

    return (
      <div className='flex-1 flex flex-row items-center gap-6pxr'>
        {operatingHourComponent}
        {operatingHourComponent && priceRangeComponent && <div className='w-1pxr h-10pxr bg-[#D9D9D9]' />}
        {priceRangeComponent}
      </div>
    );
  }, [dest.cur.poi?.operatingHour, dest.cur.poi?.priceRange]);

  useEffect(() => {
    if (dest.cur.poi?.images && dest.cur.poi.images.length > 0) return;
    if (poiImageUrl) return;

    if (retryCount >= MAX_RETRY_COUNT) return;

    function fetchPoiImages() {
      getPoiImages(dest.cur.poi?.id ?? '').then((newImages) => {
        if (newImages && newImages.length > 0) {
          setPoiImageUrl(newImages[0]?.imageUrl ?? null);
        }
      });
      setTimeout(() => {
        setRetryCount(retryCount + 1);
      }, RETRY_DELAY_MS);
    }

    fetchPoiImages();
  }, [retryCount]);

  const hasZzimcarStayUrl = useMemo(() => {
    return !!dest.cur.poi?.zzimcarStayUrl;
  }, [dest.cur.poi?.zzimcarStayUrl]);

  const onClickRedirectToZzimcarStayUrl = useCallback(() => {
    const rawUrl = dest.cur.poi?.zzimcarStayUrl;
    if (!rawUrl) return;

    const day = Number(dest.cur.day ?? dayNumber);
    const hasTripStart = !!tripFromDateTime;

    const today = dayjs().startOf('day');
    const checkinDate =
      hasTripStart && Number.isFinite(day) && day >= 1
        ? dayjs(tripFromDateTime)
            .add(day - 1, 'day')
            .startOf('day')
        : null;
    const shouldAppendDates = !!checkinDate && checkinDate.isAfter(today);

    let finalUrl = rawUrl;
    if (shouldAppendDates && checkinDate) {
      const checkoutDate = checkinDate.add(1, 'day');
      const checkin = checkinDate.format('YYYY-MM-DD');
      const checkout = checkoutDate.format('YYYY-MM-DD');

      try {
        const u = new URL(rawUrl);
        u.searchParams.set('checkin', checkin);
        u.searchParams.set('checkout', checkout);
        finalUrl = u.toString();
      } catch {
        // rawUrl 이 상대경로나 특수 스킴이라 URL() 파싱이 실패하는 경우를 대비
        const delim = rawUrl.includes('?') ? '&' : '?';
        finalUrl = `${rawUrl}${delim}checkin=${encodeURIComponent(checkin)}&checkout=${encodeURIComponent(checkout)}`;
      }
    }

    if (window?.Android) {
      window.location.href = finalUrl;
    } else {
      window.open(finalUrl, '_blank');
    }
  }, [dest.cur.day, dest.cur.poi?.zzimcarStayUrl, dayNumber, tripFromDateTime]);

  if (TRIP_CONDITIONS.IS_VIEW_MODE(mode)) {
    return (
      <div
        id={`destCard_${dest.cur.id}`}
        data-day-number={dayNumber}
        data-dest-id={dest.cur.id}
        className={`dest-card relative pl-32pxr pr-20pxr ${isLastDest ? '' : 'mb-20pxr'} z-[1] select-none`}
        style={!dest.next ? { marginBottom: 0 } : {}}
      >
        <div className='relative flex gap-20pxr z-[1]'>
          <span
            className='relative text-white rounded-full w-20pxr h-20pxr button-14-m'
            style={{
              background: getDayPinColor(dayNumber),
            }}
          >
            <span className='absolute -translate-x-1/2 -translate-y-1/2 top-1/2 left-1/2'>{destIndex + 1}</span>
          </span>
          {durationMinutes && (
            <LinkForZzimcarAndroid
              href={googleMapUrl ?? ''}
              target='_blank'
              rel='noreferrer'
              className='absolute flex items-center -translate-x-1/2 border top-1/2 pl-2pxr pr-6pxr h-24pxr rounded-l-4pxr rounded-r-20pxr border-gray-300pxr bg-point-100 min-w-48pxr left-8pxr'
            >
              <span className='w-16pxr h-16pxr p-1pxr'>
                <CarPrimary1414 className='flex-none' />
              </span>
              <span className='body-12-r text-point-400'>{durationMinutes}</span>
            </LinkForZzimcarAndroid>
          )}
          {!dest.next && isLastDay && (
            <div className='absolute bottom-0 bg-white top-20pxr left-10pxr w-1pxr height-100pxr'></div>
          )}

          <div
            className='flex-1 overflow-hidden border border-gray-200 cursor-pointer px-10pxr py-12pxr rounded-6pxr'
            onClick={() => onClickPoiDetail(dest.cur)}
          >
            <div className='flex flex-1 items-start'>
              <ImageWithFallback
                src={poiImageSrc}
                alt={dest.cur.poi?.name || ''}
                className='flex-none object-cover rounded-4pxr w-44pxr h-44pxr mr-8pxr'
                width={44}
                height={44}
                fallbackSrc={getPoiFallbackImageUrl(displayedPoiType ?? '')}
              />

              <div className='flex flex-col flex-1 gap-4pxr'>
                <div className='flex items-center gap-8pxr'>
                  <p className='text-gray-800 sub-heading-16-m line-clamp-1'>
                    {dest.cur.poi?.name}
                    <span className='hidden'>
                      {dest.cur.pos} {dest.cur.accommType}
                    </span>
                  </p>
                  {hasZzimcarStayUrl && dest.cur.accommType === 'night' && (
                    <button
                      className='bg-new-point-500 text-white body-10-r rounded-4pxr px-8pxr h-20pxr flex-none'
                      onClick={(e) => {
                        e.stopPropagation();
                        onClickRedirectToZzimcarStayUrl();
                      }}
                    >
                      찜카에서 숙소 예약
                    </button>
                  )}
                </div>
                <div className='flex items-center gap-5pxr pr-5pxr'>
                  <span className='text-gray-700 body-14-r line-clamp-1'>
                    {getPoiType({
                      poiType: dest.cur.poi?.poiType,
                      categories: dest.cur.poi?.categories,
                    })}
                  </span>
                  {dest.cur.poi?.spentTime && (
                    <div className='flex flex-row items-center flex-none bg-point-100 gap-2pxr px-2pxr rounded-4pxr h-21pxr'>
                      <SpenttimePrimary1212 className='flex-none w-14pxr h-14pxr' />
                      <span className='text-gray-700 body-14-r'>{dest.cur.poi.spentTime}시간</span>
                    </div>
                  )}
                  {rating && !dest.cur.poi?.spentTime && (
                    <div className='flex items-center flex-none h-18pxr felx gap-2pxr'>
                      <Star1212 className='flex-none' />
                      <p className='text-gray-700 body-12-r'>{rating.rating}</p>
                    </div>
                  )}
                </div>
                {poiOperatinghourAndPriceComponent}
              </div>
            </div>
            {memo !== '' && (
              <div className='pt-10pxr'>
                <div
                  className='overflow-hidden whitespace-pre-line border border-gray-200 rounded-6pxr px-8pxr py-10pxr body-14-r text-custom-black'
                  style={{ maxHeight: '62px' }}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (e.currentTarget.style.maxHeight === 'none') {
                      e.currentTarget.style.maxHeight = '62px';
                    } else {
                      e.currentTarget.style.maxHeight = 'none';
                    }
                  }}
                >
                  {memo}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  } else if (TRIP_CONDITIONS.IS_EDIT_MODE(mode)) {
    return (
      <div
        id={`destCard_${dest.cur.id}`}
        data-dest-id={dest.cur.id}
        className={`dest-card relative pl-20pxr pr-20pxr ${isLastDest ? '' : 'pb-24pxr'} ${
          dest.cur.accommType === 'morning' ? '' : 'pt-24pxr mt-36pxr'
        } z-[1] select-none`}
        style={!dest.next ? { marginBottom: 0 } : {}}
      >
        {!isSortableDragging && dest.cur.accommType !== 'morning' && (
          <div className='absolute left-20pxr right-20pxr -top-36pxr'>
            <button
              className='flex items-center justify-center w-full bg-white border border-gray-200 h-40pxr gap-4pxr rounded-4pxr mb-20pxr'
              onClick={() => {
                if (!dest.cur.day) return;
                onClickChangeMode(TRIP_MODE.SEARCH);
                setAddDestinationMode('normal');

                if (dest.cur.accommType === 'night') {
                  const destsInCurrentDay = destinations
                    .filter((_d) => _d.day === dest.cur.day && !_d.accommType)
                    .sort((a, b) => a.pos - b.pos);
                  if (destsInCurrentDay.length > 0) {
                    setAddDestinationsPosition({
                      day: dest.cur.day,
                      pos: destsInCurrentDay[destsInCurrentDay.length - 1].pos + 1,
                    });
                  } else {
                    setAddDestinationsPosition({ day: dest.cur.day, pos: 1 });
                  }
                } else {
                  setAddDestinationsPosition({
                    day: dest.cur.day,
                    pos: dest.cur.pos,
                  });
                }
              }}
            >
              <AddGray1818 className='flex-none' />
              <span className='text-gray-700 body-14-r'>장소 추가</span>
            </button>
          </div>
        )}
        <div
          className='relative flex'
          onClick={() => {
            if (dest.cur.accommType) return;
            if (dest.cur.id) {
              onToggleSelectDest(dest.cur.id);
            }
          }}
        >
          <span
            className='absolute text-white -translate-x-1/2 -translate-y-1/2 rounded-full w-20pxr h-20pxr button-14-m'
            style={{
              background: getDayPinColor(dayNumber),
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <span className='absolute -translate-x-1/2 -translate-y-1/2 top-1/2 left-1/2'>{destIndex + 1}</span>
          </span>

          <button
            className={`absolute flex items-center justify-center bg-white border border-gray-200 rounded-full w-24pxr h-24pxr`}
            style={{
              boxShadow: '0.3px 1.2px 2.5px 0px rgba(0, 0, 0, 0.20)',
              top: -10,
              right: -8,
              display: isSortableDragging ? 'none' : '',
            }}
            onClick={(e) => {
              e.stopPropagation();
              if (!dest.cur.id) return;
              onClickRemoveSlot(dest.cur.id);
            }}
          >
            <CloseGray1010 className='flex-none' />
          </button>

          <div
            className={`flex items-center flex-1 overflow-hidden cursor-pointer pl-10pxr rounded-6pxr ${
              isSelected ? 'ring-2 ring-primary' : 'ring-1 ring-gray-200'
            }`}
            onClick={() => onClickPoiDetail(dest.cur)}
          >
            <div className='flex-1 flex flex-col items-start py-12pxr pr-10pxr'>
              <div className='flex flex-1'>
                <ImageWithFallback
                  src={poiImageSrc}
                  alt={dest.cur.poi?.name || ''}
                  className='flex-none object-cover rounded-4pxr w-44pxr h-44pxr mr-8pxr'
                  width={44}
                  height={44}
                  fallbackSrc={getPoiFallbackImageUrl(displayedPoiType ?? '')}
                />

                <div className='flex flex-col flex-1 gap-4pxr'>
                  <p className='text-gray-800 sub-heading-16-m line-clamp-1'>
                    {dest.cur.poi?.name}
                    <span className='hidden'>
                      {dest.cur.pos} {dest.cur.accommType}
                    </span>
                  </p>
                  <div className='flex items-center gap-5pxr pr-5pxr'>
                    <span className='text-gray-700 body-14-r line-clamp-1'>
                      {getPoiType({
                        poiType: displayedPoiType,
                        categories: dest.cur.poi?.categories,
                      })}
                    </span>
                    {dest.cur.poi?.spentTime && (
                      <div className='flex flex-row items-center flex-none bg-point-100 gap-2pxr px-2pxr rounded-4pxr h-21pxr'>
                        <SpenttimePrimary1212 className='flex-none w-14pxr h-14pxr' />
                        <span className='text-gray-700 body-14-r'>{dest.cur.poi.spentTime}시간</span>
                      </div>
                    )}
                    {rating && !dest.cur.poi?.spentTime && (
                      <div className='flex items-center flex-none h-18pxr felx gap-2pxr'>
                        <Star1212 className='flex-none' />
                        <p className='text-gray-700 body-12-r'>{rating.rating}</p>
                      </div>
                    )}
                  </div>
                  {poiOperatinghourAndPriceComponent}
                </div>
              </div>
              {memo !== '' && (
                <div className='w-full pt-10pxr'>
                  <div
                    className='overflow-hidden whitespace-pre-line border border-gray-200 rounded-6pxr px-8pxr py-10pxr body-14-r text-custom-black'
                    style={{ maxHeight: '62px' }}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (e.currentTarget.style.maxHeight === 'none') {
                        e.currentTarget.style.maxHeight = '62px';
                      } else {
                        e.currentTarget.style.maxHeight = 'none';
                      }
                    }}
                  >
                    {memo}
                  </div>
                </div>
              )}
            </div>

            {isEditRole && !dest.cur.accommType && selectedDestIdList.length === 0 && (
              <div
                className='flex flex-col items-center justify-center h-full cursor-pointer dest-handle w-44pxr'
                style={{
                  backgroundColor: 'rgba(244, 247, 255, 0.5)',
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <DestHandlePrimary146 className='flex-none' />
              </div>
            )}
          </div>
        </div>
        {!isSortableDragging && dest.cur.accommType !== 'night' && isLastDest && (
          <div className='mt-24pxr left-20pxr right-20pxr -bottom-64pxr'>
            <button
              className='flex items-center justify-center w-full bg-white border border-gray-200 h-40pxr gap-4pxr rounded-4pxr'
              onClick={() => {
                if (!dest.cur.day) return;
                onClickChangeMode(TRIP_MODE.SEARCH);
                setAddDestinationMode('normal');
                setAddDestinationsPosition({
                  day: dest.cur.day,
                  pos: dest.cur.pos + 1,
                });
              }}
            >
              <AddGray1818 className='flex-none' />
              <span className='text-gray-700 body-14-r'>장소 추가</span>
            </button>
          </div>
        )}
      </div>
    );
  } else {
    return (
      <div
        id={`destCard_${dest.cur.id}`}
        data-dest-id={dest.cur.id}
        className={`dest-card relative pl-20pxr pr-20pxr ${isLastDest ? '' : 'pb-24pxr'} z-[1] select-none`}
        style={!dest.next ? { marginBottom: 0 } : {}}
      >
        <div className='relative flex'>
          <span
            className='absolute text-white -translate-x-1/2 -translate-y-1/2 rounded-full w-20pxr h-20pxr button-14-m'
            style={{
              background: getDayPinColor(dayNumber),
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <span className='absolute -translate-x-1/2 -translate-y-1/2 top-1/2 left-1/2'>{destIndex + 1}</span>
          </span>

          <div
            className={`flex items-center flex-1 overflow-hidden pl-10pxr rounded-6pxr min-h-80pxr ${
              isSelected ? 'border-2 border-primary' : 'border border-gray-200'
            }`}
          >
            <div className='flex flex-col flex-1 py-12pxr'>
              <div className='flex flex-1'>
                <ImageWithFallback
                  src={poiImageSrc}
                  alt={dest.cur.poi?.name || ''}
                  className='flex-none object-cover rounded-4pxr w-44pxr h-44pxr mr-8pxr'
                  width={44}
                  height={44}
                  fallbackSrc={getPoiFallbackImageUrl(displayedPoiType ?? '')}
                />
                <div className='flex flex-col flex-1 gap-4pxr'>
                  <p className='text-gray-800 sub-heading-16-m line-clamp-1'>
                    {dest.cur.poi?.name}
                    <span className='hidden'>
                      {dest.cur.pos} {dest.cur.accommType}
                    </span>
                  </p>
                  <div className='flex items-center gap-5pxr pr-5pxr'>
                    <span className='text-gray-700 body-14-r line-clamp-1'>
                      {getPoiType({
                        poiType: displayedPoiType,
                        categories: dest.cur.poi?.categories,
                      })}
                    </span>
                    {dest.cur.poi?.spentTime && (
                      <div className='flex flex-row items-center flex-none bg-point-100 gap-2pxr px-2pxr rounded-4pxr h-21pxr'>
                        <SpenttimePrimary1212 className='flex-none w-14pxr h-14pxr' />
                        <span className='text-gray-700 body-14-r'>{dest.cur.poi.spentTime}시간</span>
                      </div>
                    )}
                    {rating && !dest.cur.poi?.spentTime && (
                      <div className='flex items-center flex-none h-18pxr felx gap-2pxr'>
                        <Star1212 className='flex-none' />
                        <p className='text-gray-700 body-12-r'>{rating.rating}</p>
                      </div>
                    )}
                  </div>
                  {poiOperatinghourAndPriceComponent}
                </div>
              </div>
              <div className='relative pt-10pxr pr-10pxr'>
                <textarea
                  className='w-full border border-gray-200 outline-none resize-none memo-textarea px-8pxr py-10pxr rounded-6pxr placeholder:text-gray-500 body-14-r text-custom-black max-h-100pxr'
                  placeholder='기록하고 싶은 내용을 적어주세요 (최대 200자)'
                  value={memo}
                  rows={1}
                  onChange={(e) => {
                    if (e.target.value.length > 200) return;
                    onChangeMemo(e.target.value);
                    e.target.style.height = 'auto';
                    e.target.style.height = e.target.scrollHeight + 'px';
                  }}
                  onFocus={(e) => {
                    e.target.style.height = 'auto';
                    e.target.style.height = e.target.scrollHeight + 'px';
                  }}
                ></textarea>
              </div>
            </div>
            {isEditRole && !dest.cur.accommType && selectedDestIdList.length === 0 && (
              <div
                className='flex flex-col items-center justify-center h-full cursor-pointer dest-handle w-44pxr'
                style={{
                  backgroundColor: 'rgba(244, 247, 255, 0.5)',
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <DestHandlePrimary146 className='flex-none' />
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }
}
